import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import { usePayableApprovalsSearchStore } from "../stores/usePayableApprovalsSearchStore"
import type { PayableApprovalsSearchParams } from "../types/searchParams"

import type { Invoice } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import { useAs400SyncAutoRefresh } from "@/screens/Accounting/subfeatures/Invoices/hooks/useAs400SyncAutoRefresh"

interface UsePayableApprovalsListReturn {
  invoices: Invoice[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: PayableApprovalsSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
  handleFilterChange: <K extends keyof PayableApprovalsSearchParams>(
    key: K,
    value: PayableApprovalsSearchParams[K]
  ) => void
  handleFiltersChange: (updates: Partial<PayableApprovalsSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof PayableApprovalsSearchParams | (keyof PayableApprovalsSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
}

const ROUTE = "/app/accounting/_layout/payable-approvals"

const FILTER_KEYS: (keyof PayableApprovalsSearchParams)[] = ["dueDateFrom", "dueDateTo"]

export const usePayableApprovalsList = (): UsePayableApprovalsListReturn => {
  const store = usePayableApprovalsSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, refreshLoaded, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      refreshLoaded,
      clearStore,
    }))
  )

  const {
    data: invoices,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    refresh,
    handleScroll,
    handleSortingChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<Invoice, PayableApprovalsSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  useAs400SyncAutoRefresh(invoices, refresh)

  return {
    invoices,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
